import { api } from '../../app/api/baseApi';
import type { ProfileResponse } from '../../types/Profile';

export type AreaCode = { code?: string };

export const profileApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getUserProfile: builder.query<ProfileResponse, any>({
            query: ({ headers }) => {
                return {
                    url: '/iam/v1/users/me/profile',
                    method: 'GET',
                    ...(headers && { headers }),
                };
            },
            providesTags: ['Profile'],
        }),
        updateProfileSort: builder.mutation({
            query: (body: any) => ({
                url: `iam/v1/users/me/profile`,
                method: 'PATCH',
                body: body,
            }),
            invalidatesTags: ['Profile'],
        }),
        uploadProfileLogo: builder.mutation({
            query: (body: any) => ({
                url: `iam/v1/users/me/profile/image`,
                method: 'PATCH',
                body: body,
            }),
        }),
        downloadProfile: builder.mutation({
            query: () => ({
                url: '/iam/v1/users/me/profile/download',
                method: 'GET',
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const fileName = response.headers.get('Content-Disposition') || 'userDetails.csv';
                    return { blob, fileName };
                },
            }),
        }),
        getCountries: builder.query<object, void>({
            query: () => ({
                url: 'iam/v1/countries',
            }),
        }),
        getAreaCodes: builder.query<{ data?: AreaCode[] }, void>({
            query: () => ({
                url: 'iam/v1/countries/code',
            }),
        }),
        getLanguages: builder.query<object, void>({
            query: () => ({ url: `iam/v1/languages` }),
        }),
        getAllTimeZones: builder.query<object, void>({
            query: () => ({ url: `iam/v1/allTimezones` }),
        }),
        getDateFormats: builder.query<object, void>({
            query: () => ({ url: `iam/v1/date/format` }),
        }),
        getTimestamResolutions: builder.query<object, void>({
            query: () => ({ url: `iam/v1/timestamp/resolutions` }),
        }),
        getTempUnits: builder.query<object, void>({
            query: () => ({ url: `iam/v1/temperature/units` }),
        }),
        getSeparators: builder.query<object, void>({
            query: () => ({ url: `iam/v1/separators` }),
        }),
        getRolesPermission: builder.query({
            query: ({ roleId }) => ({
                url: `/iam/v1/roles/${roleId}`,
                method: 'GET',
            }),
        }),
        generateMobileOtp: builder.mutation<object, object>({
            query: (body: any) => {
                const { headers, ...rest } = body;
                return {
                    url: `/iam/v1/users/me/code/generate`,
                    method: 'POST',
                    body: rest,
                    ...(headers && { headers }),
                };
            },
        }),

        verifyMobileOtp: builder.mutation<object, object>({
            query: (body: any) => {
                const { headers, ...rest } = body;
                return {
                    url: `/iam/v1/users/me/code/verify`,
                    method: 'POST',
                    body: rest,
                    ...(headers && { headers }),
                };
            },
            invalidatesTags: ['Profile'],
        }),
        removeProfileImage: builder.mutation<object, void>({
            query: () => ({
                url: `/iam/v1/users/me/profile/image`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile'],
        }),
        getUserPermissions: builder.query({
            query: ({ headers, groupId, deviceId }) => {
                return {
                    url: 'iam/v1/users/me/permissions',
                    method: 'GET',
                    ...(headers && { headers }),
                    ...((groupId || deviceId) && {
                        params: deviceId ? { deviceId } : { groupId },
                    }),
                };
            },
        }),
        getUserLogoutApiCall: builder.mutation({
            query: ({ headers }) => {
                return {
                    url: 'iam/v1/users/me/audit/logout',
                    method: 'POST',
                    ...(headers && { headers }),
                };
            },
        }),
        getLoginApiCall: builder.mutation({
            query: ({ headers }) => {
                return {
                    url: 'iam/v1/users/me/audit/login',
                    method: 'POST',
                    ...(headers && { headers }),
                };
            },
        }),
        getUserPreferredLanguage: builder.query<object, any>({
            query: ({ headers }) => {
                return {
                    url: 'iam/v1/users/languagesettings',
                    method: 'GET',
                    ...(headers && { headers }),
                };
            },
            providesTags: ['PreferredLanguage'],
        }),
    }),
    overrideExisting: true,
});
export const {
    useGetDateFormatsQuery,
    useGenerateMobileOtpMutation,
    useVerifyMobileOtpMutation,
    useUploadProfileLogoMutation,
    useDownloadProfileMutation,
    useGetUserProfileQuery,
    useGetCountriesQuery,
    useGetAreaCodesQuery,
    useGetLanguagesQuery,
    useGetTimestamResolutionsQuery,
    useGetTempUnitsQuery,
    useGetSeparatorsQuery,
    useGetRolesPermissionQuery,
    useLazyGetRolesPermissionQuery,
    useRemoveProfileImageMutation,
    useGetUserPermissionsQuery,
    useGetAllTimeZonesQuery,
    useLazyGetUserPermissionsQuery,
    useUpdateProfileSortMutation,
    useGetUserLogoutApiCallMutation,
    useGetLoginApiCallMutation,
    useGetUserPreferredLanguageQuery,
} = profileApi;
